const SELLER_LABEL_ROLES = ['provider', 'operator'];

const LABEL_REFERENCE_KEYS = [
  'label_url',
  'labelUrl',
  'qr_code_url',
  'qrCodeUrl',
  'object_id',
  'objectId',
  'shippoTransactionId',
  'shippoLabelId',
];

const hasValue = value =>
  typeof value === 'string' ? value.trim().length > 0 : value !== null && value !== undefined;

const hasLabelReference = source =>
  !!source && LABEL_REFERENCE_KEYS.some(key => hasValue(source?.[key]));

const normalizeHttpUrl = value => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const parsed = new URL(trimmed);
    return ['http:', 'https:'].includes(parsed.protocol) ? trimmed : null;
  } catch (error) {
    return null;
  }
};

const firstUsableUrl = values => values.map(normalizeHttpUrl).find(Boolean) || null;

const getLabelObjectId = source => source?.objectId || source?.object_id || null;

// Shippo's saved links expire after a few days, so a label Shippo can look up goes through our
// server, which fetches a fresh link when it's clicked.
const freshLinkOr = (savedUrl, { labelLinkBase, objectId, kind }) =>
  savedUrl && labelLinkBase && objectId ? `${labelLinkBase}?kind=${kind}` : savedUrl;

const getSellerLabelState = ({ trackingInfo, protectedData, isMobileActions, labelLinkBase }) => {
  const objectId = getLabelObjectId(trackingInfo);
  const savedLabelUrl = firstUsableUrl([
    trackingInfo?.label_url,
    trackingInfo?.labelUrl,
    protectedData?.label_url,
    protectedData?.labelUrl,
  ]);
  const savedQrCodeUrl = isMobileActions
    ? firstUsableUrl([
        trackingInfo?.qr_code_url,
        trackingInfo?.qrCodeUrl,
        protectedData?.qr_code_url,
        protectedData?.qrCodeUrl,
      ])
    : null;
  const hasLabelData = hasLabelReference(trackingInfo) || hasLabelReference(protectedData);
  const downloadLabelUrl = freshLinkOr(savedLabelUrl, { labelLinkBase, objectId, kind: 'label' });
  const qrCodeUrl = freshLinkOr(savedQrCodeUrl, { labelLinkBase, objectId, kind: 'qr' });

  return { downloadLabelUrl, qrCodeUrl, hasLabelData };
};

const getCustomerReturnLabelState = ({ returnTrackingInfo, isMobileActions, labelLinkBase }) => {
  const objectId = getLabelObjectId(returnTrackingInfo);
  const downloadLabelUrl = freshLinkOr(
    firstUsableUrl([returnTrackingInfo?.label_url, returnTrackingInfo?.labelUrl]),
    { labelLinkBase, objectId, kind: 'return-label' }
  );
  const qrCodeUrl = freshLinkOr(
    isMobileActions
      ? firstUsableUrl([returnTrackingInfo?.qr_code_url, returnTrackingInfo?.qrCodeUrl])
      : null,
    { labelLinkBase, objectId, kind: 'return-qr' }
  );
  const hasLabelData = hasLabelReference(returnTrackingInfo);

  return { downloadLabelUrl, qrCodeUrl, hasLabelData };
};

export const getShippingLabelActionState = ({
  transactionRole,
  trackingInfo,
  returnTrackingInfo,
  protectedData,
  isMobileActions = false,
  labelLinkBase = null,
}) => {
  const canAccessSellerLabel = SELLER_LABEL_ROLES.includes(transactionRole);
  const canAccessReturnLabel = transactionRole === 'customer';
  const labelState = canAccessSellerLabel
    ? getSellerLabelState({ trackingInfo, protectedData, isMobileActions, labelLinkBase })
    : canAccessReturnLabel
    ? getCustomerReturnLabelState({ returnTrackingInfo, isMobileActions, labelLinkBase })
    : { downloadLabelUrl: null, qrCodeUrl: null, hasLabelData: false };

  return {
    downloadLabelUrl: labelState.downloadLabelUrl,
    qrCodeUrl: labelState.qrCodeUrl,
    showDownloadLabelLink: Boolean(labelState.downloadLabelUrl),
    showQrLabelLink: Boolean(labelState.qrCodeUrl),
    showLabelUnavailableMessage: labelState.hasLabelData && !labelState.downloadLabelUrl,
  };
};
